<template>
    <div class="custom-select-wrapper">
        <div
                class="icon-wrapper"
                v-if="iconClass"
        >
            <i
                    :class="iconClass"
                    class="select-icon"
            />
        </div>
        <div class="select-wrapper">
            <v-select
                    :append-to-body="canFlip"
                    :calculate-position="withPopper"
                    class="vselect-custom-style"
                    :class="{
                        'vselect-icon-style': iconClass,
                        'vselect-multiple': multiple,
                        'custom-class': customClass,
                        }"
                    :options="fetchedOptions"
                    :label="csLabel"
                    :reduce="reduce"
                    :multiple="multiple"
                    :components="computedComponents"
                    :loading="isLoading || showLoader"
                    :disabled="disabled"
                    :clearable="clearable"
                    :searchable="searchable"
                    :placeholder="placeholder"
                    :transition="transition"
                    :clear-search-on-select="clearSearchOnSelect"
                    :close-on-select="closeOnSelect"
                    :get-option-label="getOptionLabel"
                    :get-option-key="getOptionKey"
                    :on-tab="onTab"
                    :taggable="isTaggable"
                    :tabindex="tabindex"
                    :push-tags="pushTags"
                    :filterable="filterable"
                    :filter-by="filterBy"
                    :filter="filter"
                    :create-option="createOption"
                    :reset-on-options-change="resetOnOptionsChange"
                    :no-drop="noDrop"
                    :input-id="inputId"
                    :dir="dir"
                    :select-on-tab="selectOnTab"
                    :value="value"
                    :selectable="option => !option.isDisabled"
                    :deselect-from-dropdown="onMultipleExcludeExisting"
                    @input="handleInputEvent"
                    @change.native="handleChangeNative($event.target.value)"
                    @option:created="$emit('option:created', $event)"
                    @search="handleSearchEvent"
                    @open="handleOpenEvent"
                    @close="handleCloseEvent"
                    @option:deselected="handleDeselectedEvent"
                    @option:selecting="handleSelectedEvent"
            >
                <template v-slot:option="option">
                    <slot
                            name="our-data-select"
                            :option="option"
                            :cs-label="csLabel"
                            :is-option-preselected="isOptionPreselected"
                    >
                        <template v-if="matchPartialSearchTermWithBold">
                            <span v-html="get(option, csLabel, '')" />
                        </template>
                    </slot>
                </template>
                <template #search="{attributes, events}">
                    <input
                            class="vs__search"
                            :required="isRequired"
                            v-bind="attributes"
                            v-on="events"
                    >
                </template>

                <template #spinner="{ loading }">
                    <b-spinner
                            v-if="loading"
                            label="Loading..."
                            variant="teal"
                            small
                    />
                </template>

                <template
                        #list-footer
                        v-if="lazyLoadMode"
                >
                    <div ref="listFooter">
                        <li
                                v-if="showLoadingMoreOptions"
                                class="text-center py-2"
                        >
                            {{ lazyLoadMessage }}
                        </li>
                    </div>
                </template>

                <template
                        #no-options
                        v-if="showNoOptionsMessage"
                >
                    <li class="text-center py-2">
                        {{ noOptionsMessage }}
                    </li>
                </template>

                <template
                        #selected-option="option"
                        v-if="withCustomSelectedOption"
                >
                    <span
                            :class="`cursor-${chipCursor}`"
                            :id="option[uniqueIdentifier] || ''"
                            @mouseover.stop.prevent="$emit('option:hover', option)"
                            @mousedown.stop.prevent="$emit('option:click', option)"
                    >
                    {{ customSelectedOptionLabel(option) }}
                    </span>
                </template>
            </v-select>
        </div>
    </div>
</template>

<script>
import vSelect from 'vue-select';
import requestConfig from '../../helpers/RequestConfig.js';
import { createPopper } from '@popperjs/core';
import './CustomSelect.scss';
import has from 'lodash/has';
import get from 'lodash/get';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isEmpty from 'lodash/isEmpty';
import ShowNotificationMixin from '../../mixins/ShowNotificationMixin';
import { makeSubStringBold } from '../../helpers/stringHelpers';

export {requestConfig as SelectConfig};
export default {
    name: 'CustomSelect',
    components: {
        vSelect
    },
    extends: vSelect,
    mixins: [
        ShowNotificationMixin,
    ],
    props: {
        value: {
            type: [
                String,
                Number,
                Object,
                Array,
            ],
            required: false
        },
        iconClass: {
            type: String,
            required: false
        },
        customClass: {
            type: String,
            required: false
        },
        requestFilters: {
            type: Object,
            required: false,
            default() {
                return {};
            }
        },
        required: {
            type: Boolean,
            required: false,
            default: false
        },
        async: {
            type: Boolean,
            required: false,
            default: false
        },
        searchParam: {
            type: Array,
            required: false,
            default() {
                return [];
            }
        },
        resource: {
            type: String,
            required: false
        },
        uniqueIdentifier: {
            type: String,
            required: false,
            default: 'uuid'
        },
        chipCursor: {
            type: String,
            required: false,
            default: 'text'
        },
        csLabel: {
            type: String,
            required: false
        },
        components: {
            type: Object,
            required: false,
            default: null,
        },
        getOptionLabel: {
            type: Function,
            required: false,
            default(option) {
                if (typeof option === 'object') {
                    if (!option.hasOwnProperty(this.label)) {
                        if (get(option, this.label)) {
                            return get(option, this.label);
                        }
                        return console.warn(
                            `[vue-select warn]: Label key "option.${this.label}" does not` +
                                ` exist in options object ${JSON.stringify(option)}.\n` +
                                'https://vue-select.org/api/props.html#getoptionlabel'
                        );
                    }
                    return option[this.label];
                }
                return option;
            }
        },
        searchFilterHook: {
            type: Function,
        },
        taggable: {
            type: Boolean,
            required: false,
            default: false
        },
        isTag: {
            type: Boolean,
            required: false,
            default: false
        },
        canFlip: {
            type: Boolean,
            required: false,
            default: false
        },
        lazyLoadMode: {
            type: Boolean,
            required: false,
            default: true
        },
        lazyLoadMessage: {
            type: String,
            required: false,
            default: 'Loading more options...',
        },
        noOptionsMessage: {
            type: String,
            required: false,
            default: 'Sorry, no matching options.',
        },
        enableNoOptionsMessage: {
            type: Boolean,
            required: false,
            default: false
        },
        filterBy: {
            type: Function,
            default(option, label, search) {
                return (label || '').toLowerCase().indexOf(search.toLowerCase().trim()) > -1;
            }
        },
        extraOptions: {
            type: Array,
            default: () => []
        },
        placeholder: {
            type: String,
            default: '',
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        withCustomSelectedOption: {
            type: Boolean,
            default: false
        },
        customSelectedOptionLabel: {
            type: Function,
        },
        showLoader: {
            type: Boolean,
            default: false,
        },
        onMultipleExcludeExisting: {
            type: Boolean,
            default: true,
        },
        matchPartialSearchTermWithBold: {
            type: Boolean,
            default: false,
        },
        resetFetchedOptions: {
            type: Boolean,
            default: false,
        },
        initialized: {
            type: Boolean,
            default: false
        },
        fetchOptionsOnClick: {
            type: Boolean,
            default: false
        },
        avoidWatchRequestFilters: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            observer: null,
            defaultRequestData: {
                pagination: 'simple',
                page: 1,
                pageSize: 10,
            },
            requestData: {},
            fetchedOptions: this.options,
            isInitialized: this.initialized,
            isLoading: false,
            isTaggable: this.taggable,
            hasNextPage: false,
            currentSelectedOption: undefined,
        };
    },
    computed: {
        isRequired() {
            if (this.required) {
                return !this.value;
            }
            return false;
        },

        defaultComponents() {
            return {
                Deselect: {
                    render: createElement => createElement('span', {class: {'fal fa-times remove-icon': true}})
                },
                OpenIndicator: {
                    render: createElement => createElement('span', {class: {'far fa-chevron-down': true}})
                },
            };
        },

        computedComponents() {
            if (this.components === null) {
                return this.defaultComponents;
            } else {
                if (this.components.Deselect !== undefined && this.components.OpenIndicator === undefined) {
                    return {
                        Deselect: this.components.Deselect,
                        OpenIndicator: this.defaultComponents.OpenIndicator,
                    };
                } else if (this.components.Deselect === undefined && this.components.OpenIndicator !== undefined) {
                    return {
                        Deselect: this.defaultComponents.Deselect,
                        OpenIndicator: this.components.OpenIndicator,
                    };
                } else {
                    return this.components;
                }
            }
        },

        showLoadingMoreOptions() {
            return this.isInitialized &&
                this.lazyLoadMode &&
                this.hasNextPage;
        },

        showNoOptionsMessage() {
            return this.enableNoOptionsMessage ||
                (this.isInitialized && isEmpty(this.fetchedOptions));
        },
    },
    methods: {
        get,

        setInitialRequestData() {
            this.requestData = {
                ...cloneDeep(this.defaultRequestData),
                ...cloneDeep(this.requestFilters),
            };
        },

        async performRequest(requestData) {
            return await requestConfig.xhrLib.post(
                requestConfig.apiUrl(this.resource) + '/search',
                requestData
            );
        },

        async fetchData(withSelectedOption = false, search = null) {
            try {
                const response = await this.performRequest(this.requestData);
                this.hasNextPage = has(response.data, 'meta.nextPage') && isNumber(response.data.meta.nextPage);

                if (withSelectedOption) {
                    let fetchedOptions = response.data.data;
                    fetchedOptions.forEach((fetchedOption) => {
                        if (!this.fetchedOptions.some(
                            obj => obj[this.uniqueIdentifier] === fetchedOption[this.uniqueIdentifier])
                        ) {
                            this.fetchedOptions.push(fetchedOption);
                        }
                    });
                    return;
                }

                this.fetchedOptions = response.data.data;
                this.fetchedOptions.unshift(...this.extraOptions);
                this.isInitialized = true;

                if (this.matchPartialSearchTermWithBold && null !== search) {
                    this.generatePartialSearchTermWithBold(search);
                }
            } catch (error) {
                console.warn(error);
                this.showNotification({
                    message: 'Error while loading options.',
                    type: 'error'
                });
            }
        },

        async fetchInitialData() {
            this.setInitialRequestData();
            await this.fetchData();

            this.isLoading = false;
            this.isInitialized = true;
        },

        async fetchSelectedOption() {
            this.setInitialRequestData();
            await this.fetchData();

            let requestData = {
                ...cloneDeep(this.defaultRequestData),
                ...cloneDeep(this.requestFilters),
            };

            const extraOption = this.extraOptions.find(item => {
                return item[this.uniqueIdentifier] === this.value;
            });

            if (!this.isTag && !extraOption) {
                if (!requestData.hasOwnProperty('filter')) {
                    requestData.filter = {};
                }

                requestData.filter = this.buildSearchFilter(cloneDeep(requestData.filter), null, 'and');
            }
            try {
                const response = await this.performRequest(requestData);

                let fetchedOptions = response.data.data;
                fetchedOptions.forEach((fetchedOption) => {
                    if (!this.fetchedOptions.some(obj => obj.uuid === fetchedOption.uuid)) {
                        this.fetchedOptions.push(fetchedOption);
                    }
                });
            } catch (error) {
                console.warn(error);
                this.showNotification({
                    message: 'Error while loading options.',
                    type: 'error'
                });
            } finally {
                this.isLoading = false;
            }

            this.$emit('option:rendered');
        },

        async asyncSearch(loading, search) {
            if (this.resetFetchedOptions) {
                this.fetchedOptions = [];
            }

            this.setInitialRequestData();

            const filterHook = typeof this.searchFilterHook === 'function' ? this.searchFilterHook(search) : null;
            if (filterHook) {
                this.requestData = filterHook;
            }
            if (!this.requestData.hasOwnProperty('filter')) {
                this.requestData.filter = {};
            }

            if (!filterHook && this.multiple && this.value && this.value.length) {
                this.requestData.filter = this.buildSearchFilter(cloneDeep(this.requestData.filter), search);
            } else if (!filterHook) {
                set(this.requestData.filter, this.searchParam, search);
            }

            await this.fetchData(!this.multiple && this.value, search);
            loading(false);
        },

        buildSearchFilter(filter, search = null, andOr = 'or') {
            if (search) {
                set(filter, this.searchParam, search);
            }

            let value;
            if (Array.isArray(this.value)) {
                if (this.value.some(value => typeof value === 'object' && value !== null)) {
                    value = this.value.map(value => value[this.uniqueIdentifier]);
                } else {
                    value = this.value;
                }
            } else {
                value = [this.value];
            }

            return {
                andOr: andOr,
                filters: [
                    ...isEmpty(filter) ? [] : [filter],
                    {
                        params: {
                            [this.uniqueIdentifier]: {
                                value: value,
                                operator: 'in'
                            },
                        }
                    }
                ]
            };
        },

        async fetchMoreData([{isIntersecting, target}]) {
            if (isIntersecting) {
                this.requestData.page += 1;
                if (isNaN(this.requestData.page)) {
                    this.requestData.page = this.defaultRequestData.page += 1;
                }
                const response = await this.performRequest(this.requestData);
                this.hasNextPage = has(response.data, 'meta.nextPage') && isNumber(response.data.meta.nextPage);
                let fetchedOptions = response.data.data;

                if (isArray(fetchedOptions)) {
                    fetchedOptions.forEach((fetchedOption) => {
                        if (!this.fetchedOptions.some(obj => obj[this.uniqueIdentifier] === fetchedOption[this.uniqueIdentifier])) {
                            this.fetchedOptions.push(fetchedOption);
                        }
                    });
                }

                const ul = target.offsetParent;
                const scrollTop = target.offsetParent.scrollTop;
                await this.$nextTick();
                ul.scrollTop = scrollTop;
            }
        },

        withPopper(dropdownList, component, {width}) {
            dropdownList.style.width = width;

            const popper = createPopper(component.$refs.toggle, dropdownList, {
                placement: 'bottom',
                flipVariations: true,
            });

            return () => popper.destroy();
        },

        handleChangeNative(value) {
            if (!value) {
                return false;
            }
            if (typeof this.searchFilterHook === 'function') {
                this.isLoading = true;
                this.fetchInitialData();
            }
        },

        handleInputEvent(payload) {
            payload = this.composePayload(payload);

            this.$emit('input', payload);
            if ((payload === null || !payload.length) && this.async) {
                this.isLoading = true;
                this.fetchInitialData();
            }

            if (typeof this.searchFilterHook === 'function') {
                this.isLoading = true;
                this.fetchInitialData();
            }
        },

        composePayload(payload) {
            if (this.onMultipleExcludeExisting && this.multiple && isArray(payload)) {
                const newPayload = [];
                let optionExcluded = {
                    value: undefined,
                    count: 0,
                };

                for (let i = 0; i < payload.length; i++) {
                    if (
                        typeof this.currentSelectedOption !== 'undefined'
                        && payload[i][this.uniqueIdentifier] === this.currentSelectedOption[this.uniqueIdentifier]
                    ) {
                        optionExcluded.value = payload[i];
                        ++optionExcluded.count;
                        continue;
                    }

                    newPayload.push(payload[i]);
                }

                if (optionExcluded.count === 1) {
                    newPayload.push(optionExcluded.value);
                }

                return newPayload;
            }

            return payload;
        },

        handleSearchEvent(search, loading) {
            this.$emit('searchInput', search);
            if (!this.async) {
                return;
            }

            if (search && search.trim().length) {
                loading(true);
                this.asyncSearch(loading, search.trim(), this);
                return;
            }

            if (isEmpty(search)) {
                this.negotiateFetch();
            }
        },

        generatePartialSearchTermWithBold(search) {
            if (typeof this.fetchedOptions === 'object') {
                for (let key in this.fetchedOptions) {
                    if (
                        this.fetchedOptions.hasOwnProperty(key) &&
                        undefined !== get(this.fetchedOptions[key], this.csLabel, undefined)
                    ) {
                        set(
                            this.fetchedOptions,
                            `${key}.${this.csLabel}`,
                            makeSubStringBold(get(this.fetchedOptions[key], this.csLabel), search)
                        );
                    }
                }
            }
        },

        negotiateFetch() {
            if (this.value && this.value.length) {
                this.fetchSelectedOption();
                return;
            }
            this.fetchInitialData();
        },

        async handleOpenEvent() {
            if (this.lazyLoadMode) {
                await this.connectObserver();
            }
            if (!this.async && this.fetchOptionsOnClick && isEmpty(this.fetchedOptions)) {
                this.isLoading = true;
                this.negotiateFetch();
            }
        },

        handleCloseEvent() {
            if (this.lazyLoadMode) {
                this.disconnectObserver();
            }
        },

        async connectObserver() {
            if (this.hasNextPage) {
                await this.$nextTick();
                if (this.$refs.listFooter) {
                    this.observer.observe(this.$refs.listFooter);
                }
            }
        },

        disconnectObserver() {
            this.observer.disconnect();
        },

        async reconnectObserver() {
            this.disconnectObserver();
            await this.connectObserver();
        },

        handleDeselectedEvent(option) {
            this.$emit('option:deselected', option);
        },

        handleSelectedEvent(selectedOption) {
            this.currentSelectedOption = selectedOption;
            this.$emit('option:selected', selectedOption);
        },
        isOptionPreselected(option, csLabel) {
            let values = this.value;

            if (!Array.isArray(this.value)) {
                values = [this.value];
            }

            const optionLabel = get(option, csLabel, '');
            if (isEmpty(optionLabel)) {
                return false;
            }

            return values.some(elem => get(elem, csLabel, '') === optionLabel);
        }
    },
    created() {
        this.fetchInitialData = debounce(this.fetchInitialData, 1000);
        this.asyncSearch = debounce(this.asyncSearch, 1000);
    },
    mounted() {
        if (this.lazyLoadMode) {
            this.observer = new IntersectionObserver(this.fetchMoreData);
        }

        if (this.async) {
            this.isLoading = true;
            this.negotiateFetch();
        }
    },
    watch: {
        requestFilters: {
            deep: true,
            handler() {
                if (this.avoidWatchRequestFilters) {
                    return;
                }

                this.isLoading = true;
                this.fetchInitialData();
            }
        },
        fetchedOptions: {
            async handler(value) {
                this.$emit('fetched-options', value);
                if (this.taggable) {
                    this.isTaggable = !value.length;
                }

                if (
                    this.lazyLoadMode &&
                    this.hasNextPage &&
                    has(this.$refs, 'listFooter')
                ) {
                    await this.reconnectObserver();
                }
            }
        },
        options() {
            this.fetchedOptions = this.options;
        }
    }
};
</script>
